/**
 * "What you get behind the site" — the invite page's dashboard preview
 * (bead fnd-hi6.5).
 *
 * A small, inert mockup of the real portal — not an iframe, not a screenshot
 * of a live account. Three switchable panels (leads / change requests /
 * stats), styled to look like a portal window sitting inside the Work Order
 * page. Every panel and the primary button below the frame point at the same
 * place: `/demo?from=<token>`, the real read-only dashboard on fixtures
 * (fnd-hi6.4). The copy is careful never to imply this visitor already has
 * leads — it is a sample of what starts filling in once the site is live.
 *
 * The sample content is derived from the /demo fixtures (`demo/fixtures/
 * leads.ts`, `demo/fixtures/analytics.ts`) rather than invented separately —
 * same names, same shape of message, same 30-day framing — so a visitor who
 * clicks through does not land on a dashboard that contradicts the preview.
 */

import React, { useState } from 'react';
import { normalizeTradeKey, type TradeKey } from '../../demo/tradeJobs';
import { ArrowIcon, Eyebrow, Pill, Section, WoLinkButton } from './wo';

export interface DashboardPreviewProps {
  /** Invite token, or the demo token when `isDemo`. Missing in claim mode. */
  token?: string;
  businessName: string;
  /** Raw invite trade string (`website.trade`) — picks the sample job titles/message below. */
  trade?: string | null;
}

type TabId = 'leads' | 'changes' | 'stats';

const TABS: { id: TabId; label: string }[] = [
  { id: 'leads', label: 'Leads inbox' },
  { id: 'changes', label: 'Change requests' },
  { id: 'stats', label: 'Stats' },
];

type LeadStatus = 'new' | 'quoted' | 'won';

/**
 * Three of the eight fixture leads from `demo/fixtures/leads.ts`, trimmed to
 * what fits an inbox preview — same names, same statuses, and (via
 * `tradeJobs.ts`) the same trade-keyed job titles the /demo tour a visitor
 * lands on next actually shows, so the preview and the dashboard agree.
 */
const SAMPLE_LEAD_COPY: Record<
  TradeKey,
  { services: [string, string, string]; message: string }
> = {
  plumbing: {
    services: ['Emergency repair', 'Bathroom remodel rough-in', 'Sump pump replacement'],
    message: 'Water is coming up around the base of the toilet and the floor is wet. Can someone come out today?',
  },
  hvac: {
    services: ['No heat call', 'Ductless mini-split install', 'Furnace tune-up'],
    message: 'The furnace stopped kicking on last night and it is getting cold in the house. Can someone come out today?',
  },
  roofing: {
    services: ['Storm damage inspection', 'Gutter installation', 'Roof leak repair'],
    message: 'Shingles came off overnight and there is a wet spot spreading on the ceiling. Can someone come out today?',
  },
  electrical: {
    services: ['Outlet not working', 'Whole-house rewire', 'Panel upgrade'],
    message: 'The breaker keeps tripping and now half the kitchen has no power. Can someone come out today?',
  },
  landscaping: {
    services: ['Spring cleanup', 'Patio / hardscape quote', 'Irrigation repair'],
    message: 'A sprinkler line broke and it is flooding part of the yard. Can someone come out today?',
  },
  cleaning: {
    services: ['One-time deep clean', 'Post-construction clean', 'Move-out clean'],
    message: 'We have a move-out inspection in two days and need the place spotless. Can someone come out today?',
  },
  generic: {
    services: ['Service call', 'Follow-up quote', 'General inquiry'],
    message: 'Something came up and we need someone out as soon as possible. Can someone come out today?',
  },
};

function sampleLeads(trade?: string | null) {
  const copy = SAMPLE_LEAD_COPY[normalizeTradeKey(trade)];
  return [
    {
      name: 'Marcus Webb',
      service: copy.services[0],
      time: '3 hr ago',
      status: 'new' as LeadStatus,
      message: copy.message,
    },
    {
      name: 'Tom Ridgway',
      service: copy.services[1],
      time: '4 days ago',
      status: 'quoted' as LeadStatus,
    },
    {
      name: 'Elaine Brooks',
      service: copy.services[2],
      time: 'Last week',
      status: 'won' as LeadStatus,
    },
  ];
}

const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  quoted: 'Quoted',
  won: 'Won',
};

/** Sample bars, shaped like `demo/fixtures/analytics.ts` — small, honest, local numbers. */
const SAMPLE_STATS: { label: string; value: number; max: number; display: string }[] = [
  { label: 'Calls', value: 18, max: 30, display: '18' },
  { label: 'Quote requests', value: 9, max: 30, display: '9' },
  { label: 'Site visits', value: 340, max: 340, display: '340' },
];

function LeadsPanel({ trade }: { trade?: string | null }) {
  const leads = sampleLeads(trade);
  return (
    <ul className="wo-dp-leads">
      {leads.map((lead, index) => (
        <li key={lead.name} className={`wo-dp-lead${index === 0 ? ' is-open' : ''}`}>
          <div className="wo-dp-lead-row">
            <span className="wo-dp-lead-name">{lead.name}</span>
            <span className="wo-dp-lead-service">{lead.service}</span>
            <span className={`wo-dp-status wo-dp-status--${lead.status}`}>
              {STATUS_LABEL[lead.status]}
            </span>
            <span className="wo-dp-lead-time">{lead.time}</span>
          </div>
          {index === 0 && lead.message && (
            <p className="wo-dp-lead-msg">&ldquo;{lead.message}&rdquo;</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function ChangesPanel({ businessName }: { businessName: string }) {
  return (
    <div className="wo-dp-changes">
      <div className="wo-dp-change">
        <p className="wo-dp-change-text">
          &ldquo;Move the phone number above the fold.&rdquo;
        </p>
        <Pill ok>Done · 2h</Pill>
      </div>
      <p className="wo-dim wo-dp-change-note">
        Sent through the same box as &ldquo;Tell me what to change&rdquo; above —
        no ticket number, no waiting on hold. That is what the monthly plan
        buys once {businessName} is live.
      </p>
    </div>
  );
}

function StatsPanel() {
  return (
    <div className="wo-dp-stats">
      <p className="wo-dp-stats-period">Last 30 days · sample data</p>
      <div className="wo-dp-bars">
        {SAMPLE_STATS.map(stat => (
          <div className="wo-dp-bar-row" key={stat.label}>
            <span className="wo-dp-bar-label">{stat.label}</span>
            <div className="wo-dp-bar-track">
              <div
                className="wo-dp-bar-fill"
                style={{ width: `${Math.max(6, (stat.value / stat.max) * 100)}%` }}
              />
            </div>
            <span className="wo-dp-bar-value wo-num">{stat.display}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPreview({ token, businessName, trade }: DashboardPreviewProps) {
  const [tab, setTab] = useState<TabId>('leads');
  const demoHref = token ? `/demo?from=${encodeURIComponent(token)}` : '/demo';

  return (
    <Section>
      <Eyebrow>What you get behind the site</Eyebrow>
      <h2 style={{ marginTop: 14, maxWidth: '26ch' }}>
        A dashboard that fills in as the calls do.
      </h2>
      <p className="wo-lede" style={{ marginTop: 20, maxWidth: '40rem' }}>
        Every plan comes with a real portal — leads land here as they come in,
        change requests go through it, and you can see what the site is doing
        without asking. The panels below are sample data, not {businessName}
        &rsquo;s. The real dashboard is one click away.
      </p>

      <div className="wo-dp">
        <div className="wo-dp-bar">
          <i />
          <i />
          <i />
          <span className="wo-url">yourdashboard.foundlio.com</span>
          <Pill>Sample data</Pill>
        </div>

        <div className="wo-dp-tabs" role="tablist" aria-label="Dashboard preview">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`wo-dp-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`wo-dp-panel-${t.id}`}
              tabIndex={tab === t.id ? 0 : -1}
              className={`wo-dp-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div
          className="wo-dp-panel"
          role="tabpanel"
          id={`wo-dp-panel-${tab}`}
          aria-labelledby={`wo-dp-tab-${tab}`}
        >
          {tab === 'leads' && <LeadsPanel trade={trade} />}
          {tab === 'changes' && <ChangesPanel businessName={businessName} />}
          {tab === 'stats' && <StatsPanel />}

          <a className="wo-dp-panel-link" href={demoHref}>
            See the real thing in the demo dashboard
            <ArrowIcon />
          </a>
        </div>
      </div>

      <div className="wo-dp-cta">
        <p className="wo-dim" style={{ margin: 0 }}>
          Sample data — your dashboard fills as calls come in.
        </p>
        <WoLinkButton href={demoHref} tone="ghost">
          Open the demo dashboard
          <ArrowIcon />
        </WoLinkButton>
      </div>
    </Section>
  );
}
